import { Stack, Tabs, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import moment from 'moment';
import { useMemo } from 'react';

import { EORIGIN_DATE_FORMAT, ExitProofLogs, XML_DATE_FORMAT } from '@e-origin/shared';

import { ProofOfExitFields } from './ProofOfExitFields';

const toLocalCalendarDate = (exitDate?: Date) => moment(moment.utc(exitDate).format(XML_DATE_FORMAT), XML_DATE_FORMAT).toDate();

const getProofOfExitLogFormValues = (log: ExitProofLogs): ExitProofLogs => ({
  exitOffice: log.exitOffice || '',
  exitDate: log.exitDate ? toLocalCalendarDate(log.exitDate) : null,
  transportDocuments: log.transportDocuments ?? [],
  additionalInformation: (log.additionalInformation ?? []).map((item) => {
    const legacyPointer = (item.relation as { pointer?: number | string } | undefined)?.pointer;

    return {
      ...item,
      relation: item.relation
        ? {
            pointers: (item.relation.pointers ?? (legacyPointer !== undefined ? [legacyPointer] : [])).map(Number),
          }
        : { pointers: [] },
    };
  }),
  carrier: {
    name: log.carrier?.name || '',
    identificationNumber: log.carrier?.identificationNumber || '',
    address: {
      city: log.carrier?.address?.city || '',
      country: log.carrier?.address?.country || '',
      street: log.carrier?.address?.street || '',
      postCode: log.carrier?.address?.postCode || '',
    },
  },
});

const ProofOfExitHistoryItem: React.FC<{ log: ExitProofLogs }> = ({ log }) => {
  const form = useForm<ExitProofLogs>({
    initialValues: getProofOfExitLogFormValues(log),
  });

  return (
    <Stack align="flex-start">
      <ProofOfExitFields form={form} disabled />
    </Stack>
  );
};

const getProofOfExitLogKey = (log: ExitProofLogs, index: number) => `${log.exitOffice}-${index}`;

export const ProofOfExitHistory: React.FC<{ logs: ExitProofLogs[] }> = ({ logs }) => {
  const historyLogs = useMemo(() => [...logs].reverse(), [logs]);

  if (historyLogs.length === 0) {
    return <Text c="dimmed">No proof of exit requests sent.</Text>;
  }

  const defaultLogValue = getProofOfExitLogKey(historyLogs[0], 0);

  return (
    <Tabs defaultValue={defaultLogValue} orientation="vertical" keepMounted={false} w="100%">
      <Tabs.List mr="md">
        {historyLogs.map((log, index) => {
          const logValue = getProofOfExitLogKey(log, index);

          return (
            <Tabs.Tab key={logValue} value={logValue}>
              <Stack gap={0} align="flex-start">
                <Text fw={500}>{log.exitOffice || 'No exit office'}</Text>
                <Text c="dimmed" fz="sm">
                  {log.exitDate ? moment.utc(log.exitDate).format(EORIGIN_DATE_FORMAT) : 'No exit date'}
                </Text>
              </Stack>
            </Tabs.Tab>
          );
        })}
      </Tabs.List>

      {historyLogs.map((log, index) => {
        const logValue = getProofOfExitLogKey(log, index);

        return (
          <Tabs.Panel key={logValue} value={logValue} pl="md" w="100%">
            <ProofOfExitHistoryItem log={log} />
          </Tabs.Panel>
        );
      })}
    </Tabs>
  );
};
